import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Slider,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  VolumeUp,
  VolumeOff,
} from '@mui/icons-material';

interface AudioPreviewProps {
  src: string;
  title?: string;
  artist?: string;
  duration?: number;
  waveformData?: number[];
}

const AudioPreview: React.FC<AudioPreviewProps> = ({
  src,
  title = 'Unknown Track',
  artist = 'Unknown Artist',
  duration = 0,
  waveformData = [],
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [src]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || Array.isArray(newValue)) return;

    audio.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) return;
    setVolume(newValue);
    setIsMuted(false);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const generateWaveform = () => {
    if (waveformData.length > 0) {
      return waveformData;
    }
    
    // Generate mock waveform data for visualization
    const points = 100;
    return Array.from({ length: points }, (_, i) => {
      const base = Math.sin(i * 0.1) * 0.5 + 0.5;
      const noise = (Math.random() - 0.5) * 0.3;
      return Math.max(0.1, Math.min(1, base + noise));
    });
  };

  const waveform = generateWaveform();
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #333' }}>
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="use-credentials" />
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {artist}
        </Typography>
      </Box>

      {/* Waveform Visualization */}
      <Box sx={{ mb: 2, position: 'relative', height: 60, bgcolor: '#1a1a1a', borderRadius: 1, p: 1 }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            px: 1,
            py: 1,
          }}
        >
          {waveform.map((height, index) => {
            const isPlayed = (index / waveform.length) * 100 < progress;
            return (
              <Box
                key={index}
                sx={{
                  width: '2px',
                  height: `${height * 40}px`,
                  bgcolor: isPlayed ? '#4a9eff' : '#666',
                  borderRadius: '1px',
                  transition: 'background-color 0.1s ease',
                }}
              />
            );
          })}
        </Box>
        
        {/* Progress overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: `${progress}%`,
            bgcolor: 'rgba(74, 158, 255, 0.1)',
            borderRadius: 1,
            transition: 'width 0.1s ease',
          }}
        />
      </Box>

      {/* Progress Bar */}
      {isLoading ? (
        <LinearProgress sx={{ mb: 2, '& .MuiLinearProgress-bar': { bgcolor: '#4a9eff' } }} />
      ) : (
        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleSeek}
          sx={{
            mb: 1,
            '& .MuiSlider-thumb': {
              color: '#4a9eff',
            },
            '& .MuiSlider-track': {
              color: '#4a9eff',
            },
          }}
        />
      )}

      {/* Time Display */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {formatTime(duration)}
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handlePlayPause}
            disabled={isLoading}
            sx={{
              color: '#4a9eff',
              '&:hover': {
                bgcolor: 'rgba(74, 158, 255, 0.1)',
              },
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          
          <IconButton
            onClick={handleStop}
            disabled={isLoading}
            sx={{ ml: 1 }}
          >
            <Stop />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120 }}>
          <IconButton onClick={handleVolumeToggle} size="small">
            {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
          </IconButton>
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            sx={{
              ml: 1,
              width: 80,
              '& .MuiSlider-thumb': {
                color: '#4a9eff',
              },
              '& .MuiSlider-track': {
                color: '#4a9eff',
              },
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default AudioPreview;